import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Calendar, ArrowRight } from 'lucide-react';
import { usePageVisibility } from '../hooks/usePageVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Actualites = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageVisibility('page_actualites_enabled');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getArticleImage = (article) => article.image_url || article.cover_image || '';
  const getArticleExcerpt = (article) => article.excerpt || article.summary || article.content || '';
  const getArticleDate = (article) => article.published_at || article.created_at || new Date().toISOString();

  // Placeholder articles if none exist
  const placeholderArticles = [
    {
      id: '1',
      title: 'Lancement des Stered an Noz Awards 2026',
      excerpt: 'La première édition des awards de la musique bretonne est officiellement lancée. Découvrez les 6 catégories et comment participer.',
      content: '',
      image_url: 'https://images.unsplash.com/photo-1765738042644-a290f0a4a29f?w=800',
      created_at: new Date().toISOString()
    },
    {
      id: '2',
      title: 'Phase de propositions ouverte',
      excerpt: 'Vous pouvez dès maintenant proposer vos artistes, albums et festivals bretons préférés. La phase 1 est ouverte !',
      content: '',
      image_url: 'https://images.pexels.com/photos/33406912/pexels-photo-33406912.jpeg?w=800',
      created_at: new Date(Date.now() - 86400000).toISOString()
    },
    {
      id: '3',
      title: 'Partenaires et sponsors',
      excerpt: 'Découvrez les partenaires qui soutiennent les Stered an Noz Awards et contribuent à la promotion de la musique bretonne.',
      content: '',
      image_url: 'https://images.unsplash.com/photo-1741356474365-5f0041f89eaa?w=800',
      created_at: new Date(Date.now() - 172800000).toISOString()
    }
  ];

  const displayArticles = articles.length > 0 ? articles : placeholderArticles;

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-obsidian pt-32 pb-20" data-testid="actualites-page">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <h1 className="font-syne text-4xl md:text-6xl font-extrabold tracking-tighter uppercase text-white mb-6">
          Actualités
        </h1>
        <p className="text-lg text-white/60 max-w-2xl">
          Restez informé des dernières nouvelles concernant les Stered an Noz Awards, les artistes nominés et la scène musicale bretonne.
        </p>
      </section>

      {/* Articles Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        {displayArticles.length === 0 ? (
          <div className="text-center py-20 border border-white/10 bg-obsidian-light">
            <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">Aucune actualité pour le moment</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayArticles.map((article, index) => (
              <article
                key={article.id}
                className={`group border border-white/10 bg-obsidian-light hover:border-gold/30 transition-all overflow-hidden ${
                  index === 0 ? 'md:col-span-2 lg:col-span-2' : ''
                }`}
                data-testid={`news-article-${article.id}`}
              >
                {/* Image */}
                <div className={`relative overflow-hidden ${index === 0 ? 'aspect-[2/1]' : 'aspect-[16/10]'}`}>
                  {getArticleImage(article) ? (
                    <img
                      src={getArticleImage(article)}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-electric-blue/20 to-gold/10" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-white/40 mb-3">
                    <Calendar className="w-4 h-4" />
                    {formatDate(getArticleDate(article))}
                  </div>
                  <h2 className={`font-syne font-bold text-white mb-3 group-hover:text-gold transition-colors ${
                    index === 0 ? 'text-2xl' : 'text-xl'
                  }`}>
                    {article.title}
                  </h2>
                  <p className="text-white/60 line-clamp-3 mb-4">{getArticleExcerpt(article)}</p>
                  {article.link_url ? (
                    <a href={article.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-gold uppercase tracking-wider group-hover:gap-3 transition-all">
                      Lire la suite
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <button className="inline-flex items-center gap-2 text-sm text-gold uppercase tracking-wider group-hover:gap-3 transition-all">
                      Lire la suite
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default Actualites;
